import { FC } from "react";
import { useTranslation } from "react-i18next";
import LoadingSpinner from "src/components/reusable/LoadingSpinner";
import { calculateBarWidthPct } from "src/utils/chartBarScale";
import { getCltvSectionDefinition } from "./CltvDefinitions";
import { CltvSectionTitle } from "./CltvDashboardShared";
import "../Styles/CltvCustomerSegmentDistribution.scss";

export type CltvSegmentBarItem = {
  segmentKey: string;
  name: string;
  value: number;
  countFormattedValue?: string;
  percent?: number;
  color: string;
};

type CltvCustomerSegmentDistributionProps = {
  loading: boolean;
  pieData: CltvSegmentBarItem[];
  formatCount: (_value: number | null | undefined) => string;
};

const CltvCustomerSegmentDistribution: FC<CltvCustomerSegmentDistributionProps> = ({
  loading,
  pieData,
  formatCount,
}) => {
  const { t } = useTranslation();

  return (
    <article className="cltv-dashboard__panel cltv-dashboard__panel--segment-distribution" data-pdf-block="segment-distribution">
      <CltvSectionTitle
        title={t("cltv.sections.segmentDistribution")}
        tooltip={getCltvSectionDefinition("segmentDistribution", t)}
      />
      {loading ? (
        <LoadingSpinner compact />
      ) : pieData.length === 0 ? (
        <p className="cltv-dashboard__empty">{t("cltv.empty.noSegment")}</p>
      ) : (
        <div className="cltv-dashboard__segment-chart-wrap">
          {(() => {
            const max = pieData.reduce((m, r) => Math.max(m, r.value), 0);
            const min = pieData.filter(r => r.value > 0).reduce((m, r) => Math.min(m, r.value), max);
            return (
              <div className="cltv-dashboard__hbar-chart">
                {pieData.map(item => (
                  <div key={item.segmentKey} className="cltv-dashboard__hbar-row">
                    <span className="cltv-dashboard__hbar-label">{item.name}</span>
                    <div className="cltv-dashboard__hbar-track">
                      <div
                        className="cltv-dashboard__hbar-fill"
                        style={{
                          width: max > 0 ? `${calculateBarWidthPct(item.value, max, min)}%` : "0%",
                          backgroundColor: item.color,
                        }}
                      />
                    </div>
                    <span className="cltv-dashboard__hbar-value">
                      {item.countFormattedValue ?? formatCount(item.value)}
                    </span>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}
    </article>
  );
};

export default CltvCustomerSegmentDistribution;
